/**
 * The bloody-teddy rules as the backend plays them (slot-backend-platform `packages/game-math-bloody-teddy`, config
 * `0.0.7-battle-table-15`), as pure functions: the offline rounds are built by them, so every fixture has the shape
 * and the numbers the live server would send for its grid and its battles. The values mirror `config/base.json`;
 * the functions mirror `paylines.ts`, `win-grouping.ts`, `scatter.ts`, `vs-battle.ts` and the big-win helpers of the
 * game SDK. Presentation never computes a win: only the offline server and the fixtures' test use this.
 */

export const GRID_WIDTH = 5;
export const GRID_HEIGHT = 5;
export const GRID_CELLS = GRID_WIDTH * GRID_HEIGHT;

export const WILD = "W";
export const VS = "VS";
export const SCATTER = "FS";

/** The 19 leftmost-anchored paylines, as flat row-major cell indices (row × 5 + column), one per reel. */
export const PAYLINES: readonly { readonly id: string; readonly positions: readonly number[] }[] = [
  { id: "PL01", positions: [0, 1, 2, 3, 4] },
  { id: "PL02", positions: [5, 6, 7, 8, 9] },
  { id: "PL03", positions: [10, 11, 12, 13, 14] },
  { id: "PL04", positions: [15, 16, 17, 18, 19] },
  { id: "PL05", positions: [20, 21, 22, 23, 24] },
  { id: "PL06", positions: [0, 6, 2, 8, 4] },
  { id: "PL07", positions: [5, 11, 7, 13, 9] },
  { id: "PL08", positions: [10, 16, 12, 18, 14] },
  { id: "PL09", positions: [15, 21, 17, 23, 19] },
  { id: "PL10", positions: [5, 1, 7, 3, 9] },
  { id: "PL11", positions: [10, 6, 12, 8, 14] },
  { id: "PL12", positions: [15, 11, 17, 13, 19] },
  { id: "PL13", positions: [20, 16, 22, 18, 24] },
  { id: "PL14", positions: [0, 6, 12, 18, 24] },
  { id: "PL15", positions: [5, 11, 17, 13, 9] },
  { id: "PL16", positions: [10, 16, 22, 18, 14] },
  { id: "PL17", positions: [20, 16, 12, 8, 4] },
  { id: "PL18", positions: [15, 11, 7, 13, 19] },
  { id: "PL19", positions: [10, 6, 2, 8, 14] },
];

/** × total bet per line, for 3, 4 and 5 of a kind. W, VS and FS pay nothing of their own. */
export const PAYTABLE: Readonly<Record<string, readonly { readonly count: number; readonly multiplier: number }[]>> = {
  H1: [{ count: 3, multiplier: 0.5 }, { count: 4, multiplier: 1.2 }, { count: 5, multiplier: 2 }],
  H2: [{ count: 3, multiplier: 0.8 }, { count: 4, multiplier: 1.6 }, { count: 5, multiplier: 3 }],
  H3: [{ count: 3, multiplier: 1.2 }, { count: 4, multiplier: 2.5 }, { count: 5, multiplier: 6 }],
  H4: [{ count: 3, multiplier: 2 }, { count: 4, multiplier: 5 }, { count: 5, multiplier: 12 }],
  L1: [{ count: 3, multiplier: 0.25 }, { count: 4, multiplier: 0.5 }, { count: 5, multiplier: 1 }],
  L2: [{ count: 3, multiplier: 0.25 }, { count: 4, multiplier: 0.5 }, { count: 5, multiplier: 1 }],
  L3: [{ count: 3, multiplier: 0.25 }, { count: 4, multiplier: 0.5 }, { count: 5, multiplier: 1 }],
  L4: [{ count: 3, multiplier: 0.25 }, { count: 4, multiplier: 0.5 }, { count: 5, multiplier: 1 }],
  L5: [{ count: 3, multiplier: 0.25 }, { count: 4, multiplier: 0.5 }, { count: 5, multiplier: 1 }],
};

/** A VS battle's values: the top value is drawn from all but the last, the bottom is strictly greater. */
export const BATTLE_VALUES: readonly number[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 50, 75, 100];

/** The Super Bonus memory's cap: the pre-final ladder value, so a strictly greater bottom always exists. */
export const SAVED_TOTAL_CAP = BATTLE_VALUES[BATTLE_VALUES.length - 2]!;

/** The two free-spins sessions: the scatter count that triggers each, its spins, its retrigger. */
export const SESSIONS = {
  vsRush: { featureId: "vs_rush_buy", triggerScatterCount: 3, awardedSpins: 8, retriggerScatterCount: 3, retriggerAwardedSpins: 5, maxSpinsSafety: 100, reelSetIdentifier: "vs_rush" },
  superBonus: { featureId: "super_bonus_buy", triggerScatterCount: 4, awardedSpins: 10, retriggerScatterCount: 3, retriggerAwardedSpins: 5, maxSpinsSafety: 100, reelSetIdentifier: "super_bonus" },
} as const;
export type SessionId = keyof typeof SESSIONS;

/** The buys' prices and the modes' per-spin costs (× bet), as `/api/initial-settings` sends them. */
export const FEATURE_PRICE_MULTIPLIERS: Readonly<Record<string, number>> = { vs_rush_buy: 200, super_bonus_buy: 500 };
export const FEATURE_MODE_MULTIPLIERS: Readonly<Record<string, number>> = { bonus_hunt_mode: 2, guaranteed_vs_mode: 3 };
/** Each mode's base reel set. */
export const MODE_REEL_SETS: Readonly<Record<string, string>> = { bonus_hunt_mode: "bonus_hunt", guaranteed_vs_mode: "guaranteed_vs" };

/** The big-win tiers on a spin's win ÷ bet (floored), ascending; "huge" is the top. */
export const BIG_WIN_TIERS: readonly { readonly levelIdentifier: string; readonly minWinMultiplier: number }[] = [
  { levelIdentifier: "nice", minWinMultiplier: 15 },
  { levelIdentifier: "big", minWinMultiplier: 25 },
  { levelIdentifier: "epic", minWinMultiplier: 75 },
  { levelIdentifier: "huge", minWinMultiplier: 250 },
];

/** A round's win is capped at this × bet; reaching it ends the session. */
export const MAX_WIN_CAP = 25000;

/**
 * A buy's reveal grid before its scatters: no line can pay (reel 1 holds only high symbols, reel 2 only low ones), no
 * VS, no W. The server places the tier's 3 or 4 scatters on it, at most one per reel.
 */
export const BUY_REVEAL_BASE_GRID: readonly (readonly string[])[] = [
  ["H1", "L1", "L5", "H2", "L4"],
  ["H2", "L2", "H4", "L1", "H3"],
  ["H3", "L3", "L2", "H3", "L1"],
  ["H4", "L4", "H1", "L4", "H2"],
  ["H1", "L5", "L3", "H1", "L5"],
];

/** One paying line: its paying cells (the run's first `count`), its multiplier with and without the VS sum. */
export interface PaylineWin {
  readonly paylineId: string;
  readonly symbolId: string;
  readonly count: number;
  readonly cells: readonly number[];
  /** Paytable × the VS sum when the paying cells cross expanded columns. */
  readonly multiplier: number;
  readonly baseMultiplier: number;
  /** The sum of the crossed battles' winners; absent when no expanded column is crossed. */
  readonly vsSum?: number;
}

/**
 * Every paying line on `grid` (rows of ids) with `expanded` columns (column → its battle's winner) wild: a line starts
 * on reel 1; a scatter there breaks it (unless its column is expanded); a wild start takes the best regular symbol
 * after the VS sum; the run takes the symbol, W and any expanded column; the best paying count wins. In payline order.
 */
export function evaluatePaylines(grid: readonly (readonly string[])[], expanded: ReadonlyMap<number, number> = new Map()): PaylineWin[] {
  const flat = grid.flat();
  const wins: PaylineWin[] = [];
  for (const line of PAYLINES) {
    const first = line.positions[0]!;
    const leftmost = flat[first]!;
    const leftmostExpanded = expanded.has(first % GRID_WIDTH);
    if (!leftmostExpanded && leftmost === SCATTER) continue;
    let win: PaylineWin | undefined;
    if (leftmost === WILD || leftmostExpanded) {
      for (const symbolId of Object.keys(PAYTABLE)) {
        const candidate = lineWin(line, flat, expanded, symbolId);
        if (candidate && (!win || candidate.multiplier > win.multiplier)) win = candidate;
      }
    } else if (PAYTABLE[leftmost]) {
      win = lineWin(line, flat, expanded, leftmost);
    }
    if (win) wins.push(win);
  }
  return wins;
}

function lineWin(line: (typeof PAYLINES)[number], flat: readonly string[], expanded: ReadonlyMap<number, number>, symbolId: string): PaylineWin | undefined {
  const run: number[] = [];
  for (const position of line.positions) {
    const cell = flat[position];
    if (cell === symbolId || cell === WILD || expanded.has(position % GRID_WIDTH)) run.push(position);
    else break;
  }
  let best = { count: 0, multiplier: 0 };
  for (const match of PAYTABLE[symbolId] ?? []) if (match.count <= run.length && match.multiplier > best.multiplier) best = match;
  if (best.multiplier === 0) return undefined;
  const cells = run.slice(0, best.count);
  const vsSum = cells.reduce((sum, cell) => sum + (expanded.get(cell % GRID_WIDTH) ?? 0), 0);
  return {
    paylineId: line.id,
    symbolId,
    count: best.count,
    cells,
    multiplier: vsSum > 0 ? best.multiplier * vsSum : best.multiplier,
    baseMultiplier: best.multiplier,
    ...(vsSum > 0 ? { vsSum } : {}),
  };
}

/** bet × multiplier in minor units, the multiplier scaled by 10⁶ and the result rounded half to even (the backend's). */
export function winAmountMinor(betMinor: bigint, multiplier: number): bigint {
  if (multiplier === 0) return 0n;
  const scale = 1_000_000n;
  const product = betMinor * BigInt(Math.round(multiplier * Number(scale)));
  const quotient = product / scale;
  const twiceRemainder = (product % scale) * 2n;
  if (twiceRemainder < scale) return quotient;
  if (twiceRemainder > scale) return quotient + 1n;
  return quotient % 2n === 0n ? quotient : quotient + 1n;
}

/** One `grid_win_highlight`: the lines of one symbol that share a cell (a solo line is a group of one). */
export interface WinGroup {
  readonly symbolId: string;
  readonly cells: readonly number[];
  readonly totalMinor: bigint;
  /** The largest member count. */
  readonly count: number;
  readonly members: readonly { readonly cells: readonly number[]; readonly winMinor: bigint; readonly baseWinMinor: bigint; readonly count: number; readonly multiplierApplied: number }[];
}

/** The wins grouped as the server emits them: union-find over same-symbol lines sharing a cell, by earliest member. */
export function groupWins(wins: readonly PaylineWin[], betMinor: bigint): WinGroup[] {
  const parent = wins.map((_, i) => i);
  const find = (x: number): number => (parent[x] === x ? x : (parent[x] = find(parent[x]!)));
  for (let i = 0; i < wins.length; i++) {
    for (let j = i + 1; j < wins.length; j++) {
      if (wins[i]!.symbolId !== wins[j]!.symbolId) continue;
      const cells = new Set(wins[i]!.cells);
      if (wins[j]!.cells.some((cell) => cells.has(cell))) {
        const [a, b] = [find(i), find(j)];
        if (a !== b) parent[Math.max(a, b)] = Math.min(a, b);
      }
    }
  }
  const byRoot = new Map<number, number[]>();
  wins.forEach((_, i) => byRoot.set(find(i), [...(byRoot.get(find(i)) ?? []), i]));
  return [...byRoot.keys()]
    .sort((a, b) => a - b)
    .map((root) => {
      const members = byRoot.get(root)!.map((i) => {
        const win = wins[i]!;
        return { cells: win.cells, winMinor: winAmountMinor(betMinor, win.multiplier), baseWinMinor: winAmountMinor(betMinor, win.baseMultiplier), count: win.count, multiplierApplied: win.vsSum ?? 1 };
      });
      return {
        symbolId: wins[root]!.symbolId,
        cells: [...new Set(members.flatMap((m) => m.cells))].sort((a, b) => a - b),
        totalMinor: members.reduce((sum, m) => sum + m.winMinor, 0n),
        count: Math.max(...members.map((m) => m.count)),
        members,
      };
    });
}

/** The columns holding a VS on the landed grid, left to right (one battle each). */
export function vsColumns(grid: readonly (readonly string[])[]): number[] {
  const columns = new Set<number>();
  grid.forEach((row) => row.forEach((cell, col) => cell === VS && columns.add(col)));
  return [...columns].sort((a, b) => a - b);
}

/** The scatters' cells on the landed grid (before any expansion), ascending. */
export function scatterCells(grid: readonly (readonly string[])[]): number[] {
  return grid.flat().flatMap((cell, index) => (cell === SCATTER ? [index] : []));
}

/**
 * The reels the server teases: after the reel where the running scatter count first reaches `threshold − 1`, all the
 * reels to its right; none when that never happens or happens on the last reel.
 */
export function anticipatedColumns(scatters: readonly number[], threshold: number): number[] {
  if (threshold < 2) return [];
  const perColumn = Array.from({ length: GRID_WIDTH }, () => 0);
  for (const cell of scatters) perColumn[cell % GRID_WIDTH]! += 1;
  let cumulative = 0;
  let from: number | null = null;
  for (let col = 0; col < GRID_WIDTH; col++) {
    cumulative += perColumn[col]!;
    if (from === null && cumulative === threshold - 1) from = col + 1;
  }
  if (from === null || from >= GRID_WIDTH) return [];
  return Array.from({ length: GRID_WIDTH - from }, (_, i) => from + i);
}

/** The big-win tier of a spin's win, or none below the lowest. */
export function bigWinTier(winMinor: bigint, betMinor: bigint): string | undefined {
  if (betMinor <= 0n || winMinor <= 0n) return undefined;
  const ratio = winMinor / betMinor;
  let best: string | undefined;
  for (const tier of BIG_WIN_TIERS) {
    if (ratio >= BigInt(tier.minWinMultiplier)) best = tier.levelIdentifier;
    else break;
  }
  return best;
}

/** "Nx": the spin's win ÷ bet, floored. */
export function winRatioLabel(winMinor: bigint, betMinor: bigint): string {
  return betMinor <= 0n || winMinor <= 0n ? "0x" : `${(winMinor / betMinor).toString()}x`;
}

/** A 25-character 0/1 mask of `cells` (row-major). */
export function positionMask(cells: readonly number[]): string {
  const set = new Set(cells);
  return Array.from({ length: GRID_CELLS }, (_, i) => (set.has(i) ? "1" : "0")).join("");
}
